import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import SimplePullToRefresh from '../components/SimplePullToRefresh';
import deepLinkHandler from '../utils/deeplink';
import { 
  MapPinIcon, 
  CurrencyDollarIcon,
  CalendarIcon,
  InformationCircleIcon,
  ClipboardDocumentCheckIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  PaperClipIcon,
  XMarkIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  ArrowUturnLeftIcon,
  ArrowLeftIcon,
  DocumentDuplicateIcon,
  PhoneIcon,
  ChatBubbleLeftIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  StarIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import MessageList from '../components/MessageList';
import NotificationCenter from '../components/NotificationCenter';
import StatusChangeModal from '../components/StatusChangeModal';
import AdminStatusModal from '../components/AdminStatusModal';
import CancelledProjectModal from '../components/CancelledProjectModal';
import StatusLogsSection from '../components/StatusLogsSection';
import QuickChatSection from '../components/QuickChatSection';
import ContractInfoSection from '../components/ContractInfoSection';
import WarrantyCertificate from '../components/WarrantyCertificate';
import CancelWarningBanner from '../components/CancelWarningBanner';
import RatingModal from '../components/RatingModal';
import useStatusBarHeight from '../hooks/useStatusBarHeight';
import { statusColors, statusLabels, getStatusStyle, getStatusColor } from '../config/statusColors';

const ProjectDetail = ({ directToChat = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const statusBarHeight = useStatusBarHeight();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAdminStatusModal, setShowAdminStatusModal] = useState(false);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [showWarrantyCertificate, setShowWarrantyCertificate] = useState(false);
  const [warrantyData, setWarrantyData] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showRatingViewModal, setShowRatingViewModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [existingRating, setExistingRating] = useState(null);
  const [infoSubTab, setInfoSubTab] = useState('progress'); // 'progress' or 'chat'
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [messageFilter, setMessageFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showDeletedOptions, setShowDeletedOptions] = useState({
    image: false,
    document: false
  });
  const [viewLogsModal, setViewLogsModal] = useState({
    show: false,
    projectId: null,
    userId: null,
    userName: '',
    userType: ''
  });
  // directToChat이 true이거나 m=1 파라미터가 있거나 state에서 activeTab이 'chat'이면 chat 탭으로
  const [activeTab, setActiveTab] = useState(() => {
    // 초기값을 함수로 설정하여 최신 location.state를 확인
    const shouldShowChat = directToChat || 
      searchParams.get('m') === '1' || 
      searchParams.get('tab') === 'messages' ||
      searchParams.get('tab') === 'chat' ||
      location.state?.activeTab === 'chat';
    
    console.log('Initial activeTab calculation:', {
      directToChat,
      m: searchParams.get('m'),
      tab: searchParams.get('tab'),
      stateActiveTab: location.state?.activeTab,
      shouldShowChat
    });
    
    return shouldShowChat ? 'chat' : 'info';
  });
  const fileInputRef = useRef(null);
  const commentsEndRef = useRef(null);
  const statusModalRef = useRef(null);
  const textareaRef = useRef(null);
  
  // 딥링크 자동 실행 제거 - 앱으로 자동 전환하지 않음
  
  // 이미지 갤러리 키보드 네비게이션
  React.useEffect(() => {
    if (!showImageGallery) return;
    
    const handleKeyDown = (e) => {
      const allImages = getAllImages();
      
      if (e.key === 'ArrowLeft' && selectedImageIndex > 0) {
        setSelectedImageIndex(selectedImageIndex - 1);
        setSelectedImage(allImages[selectedImageIndex - 1]);
      } else if (e.key === 'ArrowRight' && selectedImageIndex < allImages.length - 1) {
        setSelectedImageIndex(selectedImageIndex + 1);
        setSelectedImage(allImages[selectedImageIndex + 1]);
      } else if (e.key === 'Escape') {
        setShowImageGallery(false);
        setSelectedImage(null);
        setSelectedImageIndex(0);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showImageGallery, selectedImageIndex]);
  
  // 스크롤 상태 관리
  const [scrollStates, setScrollStates] = useState({
    projectImages: { canScrollLeft: false, canScrollRight: false },
    projectDocs: { canScrollLeft: false, canScrollRight: false },
    chatImages: { canScrollLeft: false, canScrollRight: false },
    chatDocs: { canScrollLeft: false, canScrollRight: false }
  });
  
  // 파일 탭 이미지 페이지네이션 상태
  const [imageViewSettings, setImageViewSettings] = useState({
    thumbnailSize: 'medium', // 'small', 'medium', 'large'
    itemsPerPage: 20,
    currentPage: 1
  });
  
  // location.state 변경 감지
  useEffect(() => {
    if (location.state?.activeTab === 'chat') {
      setActiveTab('chat');
    }
  }, [location.state]);

  // URL 쿼리 파라미터 변경 감지
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'chat') {
      setActiveTab('chat');
    }
  }, [searchParams]);
  
  // 파일 타입 설정 가져오기
  const getFileTypeConfig = (ext) => {
    const configs = {
      pdf: { color: 'text-red-600', iconColor: 'text-red-600', bgColor: 'bg-red-100', label: 'PDF' },
      doc: { color: 'text-blue-600', iconColor: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Word' },
      docx: { color: 'text-blue-600', iconColor: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Word' },
      xls: { color: 'text-emerald-600', iconColor: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Excel' },
      xlsx: { color: 'text-emerald-600', iconColor: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Excel' },
      hwp: { color: 'text-purple-600', iconColor: 'text-purple-600', bgColor: 'bg-purple-100', label: 'HWP' },
      hwpx: { color: 'text-purple-600', iconColor: 'text-purple-600', bgColor: 'bg-purple-100', label: 'HWPX' },
      zip: { color: 'text-slate-600', iconColor: 'text-slate-600', bgColor: 'bg-slate-100', label: 'ZIP' }
    };
    return configs[ext] || { color: 'text-slate-600', iconColor: 'text-slate-600', bgColor: 'bg-slate-100', label: ext ? ext.toUpperCase() : 'FILE' };
  };

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // 기존 하이픈 제거
    const cleaned = phone.replace(/[^0-9]/g, '');
    // 하이픈 추가
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return phone;
  };

  // 스크롤 상태 확인 함수
  const checkScrollState = (containerId, stateKey) => {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    const canScrollLeft = container.scrollLeft > 0;
    const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 5;
    
    setScrollStates(prev => ({
      ...prev,
      [stateKey]: { canScrollLeft, canScrollRight }
    }));
  };

  const { data: project, isLoading, error, refetch } = useQuery(
    ['project', id],
    () => api.get(`/projects/${id}`).then(res => res.data),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 5000, // 5초 동안 캐시 사용
      cacheTime: 60000,  // 1분 동안 캐시 유지
      onError: (error) => {
        // 접속 차단된 경우 처리
        if (error.response?.status === 403 && error.response?.data?.blocked) {
          toast.error(error.response.data.message || '프로젝트 열람 기간이 종료되었습니다.');
          navigate('/projects');
        }
      }
    }
  );

  // 메시지 개수를 별도로 가져오기 (폴링 방식)
  const { data: messageCount } = useQuery(
    ['messageCount', id],
    () => api.get(`/messages/project/${id}/count`).then(res => res.data.count),
    {
      enabled: !!id,
      refetchInterval: 5000 // 5초마다 자동 갱신 (부하 경감)
    }
  );
  
  // 읽지 않은 메시지 수 조회
  const { data: unreadCount } = useQuery(
    ['unreadCount', id],
    () => api.get(`/messages/project/${id}/unread`).then(res => res.data.count),
    {
      enabled: !!id,
      refetchInterval: 3000 // 3초마다 자동 갱신
    }
  );

  // 조회 이력 조회
  const { data: viewLogs } = useQuery(
    ['viewLogs', viewLogsModal.projectId, viewLogsModal.userId],
    () => api.get(`/projects/${viewLogsModal.projectId}/view-logs/${viewLogsModal.userId}`).then(res => res.data),
    {
      enabled: viewLogsModal.show && !!viewLogsModal.projectId && !!viewLogsModal.userId,
      staleTime: 0
    }
  );

  // 메시지는 MessageList 컴포넌트에서 폴링 방식으로 처리
  // Socket.io 제거

  // 댓글 로드
  useEffect(() => {
    if (id) {
      loadComments(false);  // 초기 로드 시에는 스크롤하지 않음
      // 페이지 상단으로 이동
      window.scrollTo(0, 0);
    }
  }, [id]);

  // 프로젝트 상세 페이지 진입 시 알림 읽음 처리
  useEffect(() => {
    if (id && user) {
      // 프로젝트 관련 모든 알림을 읽음 처리
      api.post('/notifications/mark-project-read', {
        projectId: id
      }).catch(console.error);
    }
  }, [id, user]);
  
  // 프로젝트 데이터가 로드되었을 때 프로젝트 리스트 캐시 무효화
  useEffect(() => {
    if (project && user && user.user_type !== 'admin') {
      // 읽음 상태가 변경되었을 수 있으므로 프로젝트 리스트 캐시 무효화
      queryClient.invalidateQueries(['projects']);
      
      // 1초 후에 프로젝트 데이터 다시 가져오기 (읽음 상태 업데이트 확인)
      const timer = setTimeout(() => {
        refetch();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [project?.id, user, queryClient, refetch]);

  // 채팅 탭 활성화 시 메시지 알림 읽음 처리
  useEffect(() => {
    if (activeTab === 'chat' && id && user) {
      api.post('/notifications/mark-messages-read', {
        projectId: id
      }).catch(console.error);
    }
  }, [activeTab, id, user]);

  // Textarea 자동 높이 조절
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [newComment]);
  
  // 프로젝트 데이터 로드 후 초기 스크롤 상태 확인
  useEffect(() => {
    if (project) {
      setTimeout(() => {
        checkScrollState('project-images-scroll', 'projectImages');
        checkScrollState('project-docs-scroll', 'projectDocs');
        checkScrollState('chat-images-scroll', 'chatImages');
        checkScrollState('chat-docs-scroll', 'chatDocs');
      }, 100);
    }
  }, [project]);
  
  // 탭 변경 시 스크롤 상태 재확인
  useEffect(() => {
    if (activeTab === 'chat') {
      setTimeout(() => {
        checkScrollState('chat-images-scroll', 'chatImages');
        checkScrollState('chat-docs-scroll', 'chatDocs');
      }, 200);
    } else if (activeTab === 'info') {
      setTimeout(() => {
        checkScrollState('project-images-scroll', 'projectImages');
        checkScrollState('project-docs-scroll', 'projectDocs');
      }, 200);
    }
  }, [activeTab, comments]);

  // 프로젝트 완료 시 평가 상태 확인 (모든 사용자 타입)
  useEffect(() => {
    if (project?.status === 'completed' && project?.contractor?.id) {
      checkRatingStatus();
    }
  }, [project?.status, project?.contractor?.id, id]);

  const loadComments = async (shouldScrollToBottom = false, instant = false) => {
    try {
      const response = await api.get(`/messages/project/${id}`);
      setComments(response.data.messages || []);
      // 명시적으로 스크롤이 필요한 경우에만 실행
      if (shouldScrollToBottom) {
        // instant일 때는 즉시 실행, 아니면 약간의 지연
        if (instant) {
          requestAnimationFrame(() => {
            scrollToBottom(true);
          });
        } else {
          setTimeout(() => scrollToBottom(false), 100);
        }
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const scrollToBottom = (instant = false) => {
    const scrollToEnd = () => {
      // 강제 레이아웃 재계산
      void document.body.offsetHeight; // Force reflow
      
      // MessageList 내부의 스크롤 컨테이너 찾기
      const messageContainer = document.querySelector('.overflow-y-auto.px-4.py-4.pb-20');
      
      if (messageContainer) {
        // 스크롤을 최하단으로 설정
        const targetScroll = messageContainer.scrollHeight - messageContainer.clientHeight;
        messageContainer.scrollTop = targetScroll + 100; // 여유분 추가
        
        // 디버깅 정보
        console.log('Container found - scrollHeight:', messageContainer.scrollHeight, 
                    'clientHeight:', messageContainer.clientHeight,
                    'targetScroll:', targetScroll,
                    'actualScroll:', messageContainer.scrollTop);
        
        // 스크롤이 제대로 안 됐으면 다시 시도
        if (Math.abs(messageContainer.scrollTop - targetScroll) > 10) {
          setTimeout(() => {
            messageContainer.scrollTop = messageContainer.scrollHeight;
            console.log('Retry scroll to:', messageContainer.scrollTop);
          }, 100);
        }
      } else {
        console.log('Message container not found!');
      }
    };

    // 첫 번째 스크롤 시도
    scrollToEnd();
    
    // instant 모드에서 추가 처리
    if (instant && activeTab === 'chat') {
      // ResizeObserver로 컨테이너 크기 변경 감지
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          // 크기가 변경되면 스크롤
          requestAnimationFrame(scrollToEnd);
        }
      });
      
      // 메시지 컨테이너 관찰 시작
      const messageContainer = document.querySelector('.overflow-y-auto.px-4.py-4.pb-20');
      if (messageContainer) {
        resizeObserver.observe(messageContainer);
      }
      
      // 이미지 로드 대기를 위한 반복 스크롤
      let scrollCount = 0;
      let lastScrollHeight = 0;
      let stabilityCount = 0; // 안정성 카운터
      
      const scrollInterval = setInterval(() => {
        const msgContainer = document.querySelector('.overflow-y-auto.px-4.py-4.pb-20');
        if (msgContainer) {
          const currentScrollHeight = msgContainer.scrollHeight;
          
          // 높이가 변경되었으면 스크롤
          if (currentScrollHeight !== lastScrollHeight) {
            msgContainer.scrollTop = msgContainer.scrollHeight;
            lastScrollHeight = currentScrollHeight;
            stabilityCount = 0; // 리셋
          } else {
            stabilityCount++;
          }
        }
        
        scrollCount++;
        
        // 30회 시도 후 중지 (약 3초) 또는 5회 연속 높이 변화 없으면 중지
        if (scrollCount >= 30 || stabilityCount >= 5) {
          clearInterval(scrollInterval);
          resizeObserver.disconnect();
          // 마지막 한 번 더 스크롤
          requestAnimationFrame(() => {
            scrollToEnd();
            // 0.5초 후 최종 확인
            setTimeout(scrollToEnd, 500);
          });
        }
      }, 100);
      
      // 모든 이미지 완전 로드 대기
      setTimeout(() => {
        const images = document.querySelectorAll('img');
        const imagePromises = Array.from(images).map(img => {
          if (img.complete) {
            return Promise.resolve();
          }
          return new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
            // 10초 타임아웃
            setTimeout(resolve, 10000);
          });
        });
        
        // 모든 이미지 로드 완료 후 최종 스크롤
        Promise.all(imagePromises).then(() => {
          requestAnimationFrame(() => {
            scrollToEnd();
            // 한 번 더 확인
            setTimeout(scrollToEnd, 200);
          });
        });
      }, 100);
    }
  };

  // 필터링된 메시지 가져오기
  const getFilteredMessages = () => {
    if (messageFilter === 'all') return comments;
    
    return comments.filter(comment => {
      if (!comment.MessageFiles || comment.MessageFiles.length === 0) return false;
      
      if (messageFilter === 'images') {
        return comment.MessageFiles.some(file => file.file_type === 'image');
      } else if (messageFilter === 'documents') {
        return comment.MessageFiles.some(file => file.file_type !== 'image');
      }
      return true;
    });
  };

  // 모든 이미지 가져오기
  const getAllImages = () => {
    const images = [];
    comments
      .filter(comment => !comment.is_deleted || user?.user_type === 'admin')
      .forEach(comment => {
        if (comment.MessageFiles) {
          comment.MessageFiles.forEach(file => {
            if (file.file_type === 'image') {
              images.push({
                ...file,
                userName: comment.User?.name,
                isDeleted: comment.is_deleted,  // 삭제 상태 추가
                userType: comment.User?.user_type,
                date: comment.created_at
              });
            }
          });
        }
      });
    return images;
  };

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusModalRef.current && !statusModalRef.current.contains(event.target)) {
        setShowStatusModal(false);
      }
    };

    if (showStatusModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusModal]);

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'yyyy.MM.dd HH:mm', { locale: ko });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  const statusConfig = {
    received: { 
      label: statusLabels.received, 
      icon: InformationCircleIcon, 
      gradient: getStatusColor('received', 'gradient'),
      bgColor: getStatusColor('received', 'primary'),
      textColor: getStatusColor('received', 'textOnPrimary')
    },
    review: { 
      label: statusLabels.review, 
      icon: ClipboardDocumentCheckIcon, 
      gradient: getStatusColor('review', 'gradient'),
      bgColor: getStatusColor('review', 'primary'),
      textColor: getStatusColor('review', 'textOnPrimary')
    },
    contract: { 
      label: statusLabels.contract, 
      icon: DocumentTextIcon, 
      gradient: getStatusColor('contract', 'gradient'),
      bgColor: getStatusColor('contract', 'primary'),
      textColor: getStatusColor('contract', 'textOnPrimary')
    },
    completed: { 
      label: statusLabels.completed, 
      icon: CheckCircleIcon, 
      gradient: getStatusColor('completed', 'gradient'),
      bgColor: getStatusColor('completed', 'primary'),
      textColor: getStatusColor('completed', 'textOnPrimary')
    },
    cancelled: { 
      label: statusLabels.cancelled, 
      icon: XCircleIcon, 
      gradient: getStatusColor('cancelled', 'gradient'),
      bgColor: getStatusColor('cancelled', 'primary'),
      textColor: getStatusColor('cancelled', 'textOnPrimary')
    }
  };

  // 상태 변경 버튼 클릭 핸들러
  const handleStatusButtonClick = () => {
    if (project?.status === 'completed') {
      // 완료 상태일 때는 보증서 확인 기능으로 연결
      fetchWarrantyCertificate();
    } else if (project?.status === 'cancelled') {
      // 취소 상태일 때는 모달로 안내 메시지 표시
      setShowCancelledModal(true);
    } else {
      // 다른 상태일 때는 상태 변경 모달 표시
      setShowStatusModal(true);
    }
  };

  const handleStatusChange = async (statusData) => {
    try {
      // 계약서 파일 분리
      const contractFiles = statusData.contractFiles;
      delete statusData.contractFiles;
      
      // 상태 변경과 함께 로그 정보 전송
      await api.put(`/projects/${id}`, statusData);
      
      // 계약서 파일이 있으면 업로드
      if (contractFiles && contractFiles.length > 0) {
        const formData = new FormData();
        formData.append('category', 'contract');
        contractFiles.forEach(file => {
          formData.append('files', file);
        });
        
        await api.post(`/projects/${id}/files`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('상태가 변경되었습니다');
      refetch();
      setShowStatusModal(false);
      setShowAdminStatusModal(false);
      
      // 상태 로그 새로고침
      queryClient.invalidateQueries(['statusLogs', id]);
    } catch (error) {
      console.error('Status change error:', error);
      toast.error('상태 변경에 실패했습니다');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && selectedFiles.length === 0) return;

    try {
      const formData = new FormData();
      formData.append('content', newComment.trim());
      
      if (replyTo) {
        formData.append('reply_to_id', replyTo.id);
      }
      
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post(`/messages/project/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        console.log('Message sent successfully:', response.data);
        
        setNewComment('');
        setSelectedFiles([]);
        setReplyTo(null);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // 파일이 업로드되었으면 프로젝트 정보 새로고침
        if (selectedFiles.length > 0) {
          refetch();
          loadComments(true);  // 메시지 전송 후에는 스크롤
        }
        
        // 메시지 전송 후 스크롤
        setTimeout(() => scrollToBottom(false), 100);
      }
    } catch (error) {
      console.error('Message submit error:', error);
      toast.error('메시지 작성에 실패했습니다');
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setActiveTab('chat');
    textareaRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-blue-600">프로젝트 로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-900 mb-2">프로젝트를 찾을 수 없습니다</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary mt-4"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = statusConfig[project.status];
  const filteredMessages = getFilteredMessages();

  // 메시지 삭제 핸들러
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('메시지를 삭제하시겠습니까?\n삭제된 메시지는 복구할 수 없습니다.')) {
      return;
    }

    try {
      await api.delete(`/messages/${messageId}`);
      
      // 메시지 목록 새로고침
      await loadComments();
      
      toast.success('메시지가 삭제되었습니다');
    } catch (error) {
      console.error('메시지 삭제 오류:', error);
      toast.error('메시지 삭제에 실패했습니다');
    }
  };

  // Pull to Refresh 핸들러
  const handleRefresh = async () => {
    await Promise.all([
      refetch(),
      loadComments()
    ]);
  };
  
  // 전체 다운로드 핸들러
  const handleBulkDownload = async (category, includeDeleted = false) => {
    try {
      // 모바일 체크
      const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
      
      if (isMobile && window.Android) {
        // Android WebView에서 처리
        toast.info('파일 다운로드를 시작합니다...');
        window.Android.downloadBulkFiles(project.id, category);
      } else {
        // PC 브라우저에서 처리
        toast.info('ZIP 파일을 생성 중입니다...');
        
        const response = await api.get(`/projects/${project.id}/files/download-zip`, {
          params: { category, includeDeleted },
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Blob을 URL로 변환
        const url = window.URL.createObjectURL(new Blob([response.data]));
        
        // 다운로드 링크 생성
        const link = document.createElement('a');
        link.href = url;
        
        // 파일명 생성
        const categoryName = {
          'initial': '프로젝트첨부파일',
          'image': '소통이미지',
          'document': '소통문서',
          'contract': '계약서',
          'message': '소통파일',
          'all': '전체파일'
        }[category] || '파일';
        
        link.download = `${project.title}_${categoryName}_${new Date().getTime()}.zip`;
        
        // 다운로드 실행
        document.body.appendChild(link);
        link.click();
        
        // 정리
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
        
        toast.success('다운로드가 시작되었습니다');
      }
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('다운로드 중 오류가 발생했습니다');
    }
  };
  
  // 보증서 데이터 가져오기
  const fetchWarrantyCertificate = async () => {
    try {
      const response = await api.get(`/warranties/certificate/${id}`);
      setWarrantyData(response.data);
      setShowWarrantyCertificate(true);
    } catch (error) {
      console.error('보증서 조회 오류:', error);
      toast.error('보증서를 불러오는데 실패했습니다');
    }
  };

  // 평가 여부 확인
  const checkRatingStatus = async () => {
    if (!project?.contractor?.id || !id) return;
    
    try {
      const response = await api.get(`/reviews/projects/${id}/check`);
      setHasRated(response.data.hasReview);
      if (response.data.hasReview && response.data.review) {
        setExistingRating({
          rating: response.data.review.rating,
          comment: response.data.review.comment,
          created_at: response.data.review.created_at
        });
      }
    } catch (error) {
      console.error('평가 상태 확인 오류:', error);
    }
  };

  return (
    <SimplePullToRefresh onRefresh={handleRefresh} refreshMessage="프로젝트 정보가 업데이트되었습니다!" showDataCount={false}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* 통합 헤더 영역 - 완전 불투명 */}
      <div className="sticky z-[60] bg-white shadow-sm" style={{ top: `${statusBarHeight}px` }}>
        {/* 프로젝트 헤더 */}
        <div className="px-3 py-2 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                console.log('뒤로가기 클릭 - location.state:', location.state);
                queryClient.invalidateQueries(['projects']);
                
                // 고객 또는 VIP 고객인 경우 프로젝트 개수 확인
                if (user?.user_type === 'customer' || user?.user_type === 'vip_customer') {
                  try {
                    const response = await api.get('/projects?my=true');
                    const projects = response.data.projects || [];
                    
                    // 프로젝트가 1개인 경우 홈으로 이동
                    if (projects.length === 1) {
                      navigate('/');
                    } else {
                      // location.state에서 이전 페이지 정보 확인
                      if (location.state?.from === '/projects') {
                        const params = new URLSearchParams();
                        // 페이지 번호가 있으면 추가 (1페이지도 포함)
                        if (location.state?.page) {
                          params.append('page', String(location.state.page));
                        }
                        if (location.state?.status) params.append('status', location.state.status);
                        if (location.state?.search) params.append('search', location.state.search);
                        
                        const queryString = params.toString();
                        navigate(`/projects${queryString ? `?${queryString}` : ''}`);
                      } else {
                        navigate('/projects');
                      }
                    }
                  } catch (error) {
                    console.error('프로젝트 목록 조회 실패:', error);
                    navigate('/projects');
                  }
                } else {
                  // 관리자나 소장인 경우
                  if (location.state?.from === '/projects') {
                    const params = new URLSearchParams();
                    // 페이지 번호가 있으면 추가 (1페이지도 포함)
                    if (location.state?.page) {
                      params.append('page', String(location.state.page));
                    }
                    if (location.state?.status) params.append('status', location.state.status);
                    if (location.state?.search) params.append('search', location.state.search);
                    
                    const queryString = params.toString();
                    navigate(`/projects${queryString ? `?${queryString}` : ''}`);
                  } else {
                    navigate('/projects');
                  }
                }
              }}
              className="p-1.5 -ml-1 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 text-blue-600" />
            </button>
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <h1 className="text-sm font-semibold text-gray-900 truncate">
                {project.title}
              </h1>
              <span className="text-xs text-gray-500">•</span>
              <p className="text-xs text-gray-500 truncate">{project.construction_types}</p>
            </div>
            {/* 알림 아이콘 */}
            <NotificationCenter />
            {/* 상태 표시 */}
            <button
              onClick={handleStatusButtonClick}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${currentStatus.bgColor} ${currentStatus.textColor} hover:opacity-90`}
              disabled={user?.user_type !== 'admin' && user?.user_type !== 'contractor'}
            >
              <currentStatus.icon className="h-3.5 w-3.5" />
              <span>{currentStatus.label}</span>
            </button>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="border-b border-blue-100">
          <div className="relative">
            <div className="px-2">
              <div className="flex">
                {[
                  { id: 'info', label: '정보', icon: InformationCircleIcon },
                  { id: 'chat', label: '소통', count: messageCount || 0, unread: unreadCount || 0, icon: ChatBubbleBottomCenterTextIcon },
                  { id: 'files', label: '파일', count: getAllImages().length + comments.filter(comment => !comment.is_deleted || user?.user_type === 'admin').flatMap(comment => comment.MessageFiles || []).filter(file => file.file_type === 'document').length, icon: PhotoIcon }
                ].map((tab, index) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      // 현재 탭이 소통이고, 다른 탭으로 이동할 때 즉시 최상단으로
                      if (activeTab === 'chat' && tab.id !== 'chat') {
                        window.scrollTo(0, 0); // 즉시 최상단으로
                      }
                      
                      // 모든 탭을 동일하게 처리 - 즉시 탭 상태 변경
                      setActiveTab(tab.id);
                      
                      // 소통 탭을 클릭한 경우에만 추가 처리
                      if (tab.id === 'chat' && activeTab !== 'chat') {
                        // 메시지 로드는 별도로 처리 (useEffect에서 처리됨)
                        setTimeout(() => {
                          loadComments(true, true);
                        }, 100);
                      }
                    }}
                    className={`flex-1 relative group transition-all duration-300`}
                  >
                    <div className={`flex items-center justify-center gap-1.5 py-3 transition-all duration-300 ${
                      activeTab === tab.id
                        ? ''
                        : ''
                    }`}>
                      <tab.icon className={`h-4 w-4 transition-all duration-300 ${
                        activeTab === tab.id 
                          ? 'text-blue-600' 
                          : 'text-gray-400 group-hover:text-gray-600'
                      }`} />
                      <span className={`text-sm font-semibold transition-all duration-300 ${
                        activeTab === tab.id 
                          ? 'text-blue-600' 
                          : 'text-gray-500 group-hover:text-gray-700'
                      }`}>
                        {tab.label}
                      </span>
                      {tab.count !== undefined && (
                        <div className="flex items-center gap-1">
                          {tab.count > 0 && (
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full transition-all duration-300 ${
                              activeTab === tab.id 
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-blue-100 text-blue-600 group-hover:bg-blue-200'
                            }`}>
                              {tab.count > 999 ? '999+' : tab.count}
                            </span>
                          )}
                          {tab.unread > 0 && (
                            <div className="relative">
                              <span className="absolute -top-0.5 -right-0.5 block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                              <span className="block h-2 w-2 rounded-full bg-red-500" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* 하단 인디케이터 */}
                    <div className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full transition-all duration-300 ${
                      activeTab === tab.id
                        ? 'bg-blue-600'
                        : 'bg-transparent'
                    }`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 컨텐츠 */}
      <div className="flex-1">
        {/* 프로젝트 정보 탭 */}
        {activeTab === 'info' && (
          <div className="p-4 space-y-4">
            {/* 취소 경고 배너 - 프로젝트 현황 위에 표시 */}
            {project?.status === 'cancelled' && (
              <CancelWarningBanner 
                project={project} 
                userType={user?.user_type} 
              />
            )}
            
            {/* 상태 및 기본 정보 셉션 */}
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900">프로젝트 현황</h2>
                  {user.user_type === 'admin' && (
                    <button
                      onClick={() => setShowAdminStatusModal(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="상태 직접 변경"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {(user.user_type === 'admin' || user.user_type === 'contractor') && (
                  <button
                    onClick={handleStatusButtonClick}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentStatus.bgColor} ${currentStatus.textColor} hover:opacity-90`}
                  >
                    {project?.status === 'completed' ? (
                      <>
                        <currentStatus.icon className="h-4 w-4" />
                        <span>{currentStatus.label}</span>
                      </>
                    ) : (
                      <>
                        <currentStatus.icon className="h-4 w-4" />
                        <span>{currentStatus.label}</span>
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* 평가 유도 배너 - 프로젝트 완료 + 고객/VIP + 미평가 시 표시 */}
              {project.status === 'completed' && 
               (user?.user_type === 'customer' || user?.user_type === 'vip_customer') && 
               !hasRated && (
                <div className="mb-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-3 sm:p-4 border border-blue-300 shadow-lg animate-pulse">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 p-2 bg-yellow-400 rounded-lg">
                        <StarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-800" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm sm:text-base font-bold text-white">⭐ 프로젝트가 완료되었습니다!</p>
                        <p className="text-xs sm:text-sm text-blue-100 mt-0.5">소장님의 작업은 어떠셨나요? 평가해주세요</p>
                      </div>
                    </div>
                    <div className="flex justify-end sm:justify-start">
                      <button
                        onClick={() => setShowRatingModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 sm:px-4 sm:py-2 bg-yellow-400 text-yellow-900 text-sm font-bold rounded-lg hover:bg-yellow-300 transition-colors shadow-sm whitespace-nowrap animate-bounce"
                      >
                        <StarIcon className="h-4 w-4" />
                        평가하기
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 보증서 확인 배너 - 프로젝트가 완료 상태일 때만 표시 */}
              {project.status === 'completed' && (
                <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 sm:p-4 border border-green-200 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 p-2 bg-green-500 rounded-lg">
                        <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm sm:text-base font-bold text-green-900">✅ 프로젝트가 완료되었습니다</p>
                        <p className="text-xs sm:text-sm text-green-700 mt-0.5">보증서를 확인하여 하자보수 기간을 확인하세요</p>
                      </div>
                    </div>
                    <div className="flex justify-end sm:justify-start">
                      <button
                        onClick={fetchWarrantyCertificate}
                        className="inline-flex items-center gap-2 px-4 py-2.5 sm:px-4 sm:py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm whitespace-nowrap"
                      >
                        <ShieldCheckIcon className="h-4 w-4" />
                        보증서 확인
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {project.address && (
                  <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <MapPinIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">위치</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{project.address}</p>
                    </div>
                  </div>
                )}
                
                {(project.contract_amount || project.estimated_amount) && (
                  <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <CurrencyDollarIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">{project.contract_amount ? '계약금액' : '예상금액'}</p>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(project.contract_amount || project.estimated_amount)}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <CalendarIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">접수일</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(project.created_at)}</p>
                  </div>
                </div>

                
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                  <WrenchScrewdriverIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">공사유형</p>
                    <p className="text-sm font-medium text-gray-900">{project.construction_types}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 참여자 정보 - 관리자와 VIP는 항상 표시, 일반 사용자는 취소 시 숨김 */}
            {(user?.user_type === 'admin' || user?.user_type === 'vip_customer' || project.status !== 'cancelled') ? (
              <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">참여자</h3>
                <div className="space-y-3">
                  {project.contractor && (
                  <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      소
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 inline-flex items-center gap-1">
                          {project.contractor.name} 소장님
                          {user?.user_type === 'admin' && (
                            <button
                              onClick={() => {
                                setViewLogsModal({
                                  show: true,
                                  projectId: project.id,
                                  userId: project.contractor.id,
                                  userName: project.contractor.name,
                                  userType: 'contractor'
                                });
                              }}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                project.contractor_view_count > 0 
                                  ? (project.contractor_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                  : 'bg-blue-100 text-blue-500 hover:bg-blue-200'
                              }`}
                              title="클릭하여 조회 이력 보기"
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                project.contractor_view_count > 0 
                                  ? (project.contractor_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                                  : 'bg-blue-400'
                              }`}></span>
                              <span>{project.contractor_view_count || 0}</span>
                            </button>
                          )}
                        </p>
                        
                        {/* 평가 관련 버튼 - 모든 사용자 타입에서 평가 정보 표시 */}
                        {project.status === 'completed' && hasRated && (
                          // 평가가 있는 경우 별점 표시 (모든 사용자 타입)
                          <button
                            onClick={() => {
                              // 고객/VIP는 평가 수정, 소장/관리자는 평가 보기
                              if (user?.user_type === 'customer' || user?.user_type === 'vip_customer') {
                                setShowRatingModal(true);
                              } else {
                                setShowRatingViewModal(true);
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                            title={
                              user?.user_type === 'customer' || user?.user_type === 'vip_customer' 
                                ? (existingRating?.comment ? `평가: ${existingRating.comment}` : '평가를 수정하려면 클릭하세요')
                                : '고객 평가 내용을 확인하려면 클릭하세요'
                            }
                          >
                            <CheckCircleIcon className="h-3 w-3 text-green-600 hidden sm:inline-block" />
                            <div className="flex items-center gap-0.5">
                              <StarIcon className="h-3 w-3 text-yellow-500 fill-current" />
                              <span className="text-xs font-medium text-green-700">
                                {existingRating?.rating || 5}.0
                              </span>
                            </div>
                            <span className="hidden sm:inline text-xs font-medium text-green-700">평가완료</span>
                          </button>
                        )}
                        
                        {/* 고객/VIP만 평가하기 버튼 표시 */}
                        {project.status === 'completed' && 
                         !hasRated &&
                         (user?.user_type === 'customer' || user?.user_type === 'vip_customer') && (
                          <button
                            onClick={() => setShowRatingModal(true)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium hover:bg-yellow-200 transition-colors border border-yellow-300"
                            title="소장님을 평가해주세요"
                          >
                            <StarIcon className="h-3 w-3" />
                            평가하기
                          </button>
                        )}
                      </div>
                      {project.contractor.phone && (
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`tel:${project.contractor.phone}`}
                            className="p-1.5 bg-white/70 rounded-lg hover:bg-white transition-all group shadow-sm"
                            title={formatPhoneNumber(project.contractor.phone)}
                          >
                            <PhoneIcon className="h-4 w-4 text-blue-600 group-hover:text-blue-700" />
                          </a>
                          <a
                            href={`sms:${project.contractor.phone}`}
                            className="p-1.5 bg-white/70 rounded-lg hover:bg-white transition-all group shadow-sm"
                            title="문자 보내기"
                          >
                            <ChatBubbleLeftIcon className="h-4 w-4 text-blue-600 group-hover:text-blue-700" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {project.customer && (
                  <div className="flex items-center gap-3 p-2 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      고
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 inline-flex items-center gap-1">
                        {project.customer.name} 고객님
                        {user?.user_type === 'admin' && (
                          <button
                            onClick={() => {
                              setViewLogsModal({
                                show: true,
                                projectId: project.id,
                                userId: project.customer.id,
                                userName: project.customer.name,
                                userType: 'customer'
                              });
                            }}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                              project.customer_view_count > 0 
                                ? (project.customer_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                : 'bg-blue-100 text-blue-500 hover:bg-blue-200'
                            }`}
                            title="클릭하여 조회 이력 보기"
                          >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                              project.customer_view_count > 0 
                                ? (project.customer_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                                : 'bg-blue-400'
                            }`}></span>
                            <span>{project.customer_view_count || 0}</span>
                          </button>
                        )}
                      </p>
                      {project.customer.phone && (
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`tel:${project.customer.phone}`}
                            className="p-1.5 bg-white/70 rounded-lg hover:bg-white transition-all group shadow-sm"
                            title={formatPhoneNumber(project.customer.phone)}
                          >
                            <PhoneIcon className="h-4 w-4 text-green-600 group-hover:text-green-700" />
                          </a>
                          <a
                            href={`sms:${project.customer.phone}`}
                            className="p-1.5 bg-white/70 rounded-lg hover:bg-white transition-all group shadow-sm"
                            title="문자 보내기"
                          >
                            <ChatBubbleLeftIcon className="h-4 w-4 text-green-600 group-hover:text-green-700" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
              </div>
            ) : (
              /* 취소된 프로젝트 - 참여자 정보 대체 메시지 */
              <div className="bg-gray-50 rounded-2xl shadow-sm border border-gray-200 p-4">
                <div className="text-center py-2">
                  <InformationCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    취소된 프로젝트는 참여자 정보가 비공개 처리됩니다
                  </p>
                </div>
              </div>
            )}

            {/* 진행 현황 & 실시간 소통 탭 */}
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100">
              {/* 탭 헤더 */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setInfoSubTab('progress')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
                    infoSubTab === 'progress'
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📋 진행 현황
                  {infoSubTab === 'progress' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                  )}
                </button>
                <button
                  onClick={() => setInfoSubTab('chat')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
                    infoSubTab === 'chat'
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  💬 실시간 소통
                  {infoSubTab === 'chat' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                  )}
                </button>
              </div>
              
              {/* 탭 컨텐츠 */}
              <div className="p-4">
                {infoSubTab === 'progress' ? (
                  <StatusLogsSection projectId={id} projectFiles={project?.ProjectFiles} />
                ) : (
                  <QuickChatSection 
                    projectId={id} 
                    onNavigateToChat={() => {
                      setActiveTab('chat');
                      requestAnimationFrame(() => {
                        loadComments(true, true); // 전체 대화 보기 클릭 시 즉시 스크롤
                      });
                    }}
                  />
                )}
              </div>
            </div>

            {/* 계약 정보 섹션 - 계약 상태일 때만 표시 */}
            {project?.status === 'contract' && (
              <ContractInfoSection project={project} onUpdate={refetch} onBulkDownload={handleBulkDownload} />
            )}

            {/* 프로젝트 첨부파일 (등록 시 첨부) */}
            {project?.ProjectFiles && project.ProjectFiles.filter(file => file.category === 'initial').length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900">프로젝트 첨부파일</h3>
                  <span className="text-xs text-gray-500">등록 시 첨부된 파일</span>
                </div>
                
                {/* 프로젝트 이미지 */}
                {(() => {
                  const projectImages = project.ProjectFiles.filter(file => file.category === 'initial' && file.file_type === 'image');
                  const projectDocs = project.ProjectFiles.filter(file => file.category === 'initial' && file.file_type === 'document');
                  
                  return (
                    <>
                      {projectImages.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-2">이미지 ({projectImages.length}개)</p>
                          <div className="relative group">
                            <div 
                              id="project-images-scroll"
                              className="flex gap-2 overflow-x-auto scrollbar-hide pb-2"
                              style={{ scrollBehavior: 'smooth' }}
                              onScroll={() => checkScrollState('project-images-scroll', 'projectImages')}
                            >
                              {projectImages.map((file, index) => (
                                <div
                                  key={index}
                                  className="relative w-20 h-20 flex-shrink-0 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity select-none border border-blue-100"
                                  onClick={() => {
                                    const allImages = getAllImages();
                                    const index = allImages.findIndex(img => img.id === file.id);
                                    setSelectedImage(file);
                                    setSelectedImageIndex(index >= 0 ? index : 0);
                                    setShowImageGallery(true);
                                  }}
                                >
                                  <picture>
                                    {file.thumbnail_small_webp && (
                                      <source 
                                        srcSet={`/${file.thumbnail_small_webp.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                        type="image/webp" 
                                      />
                                    )}
                                    <img
                                      src={`/${(file.thumbnail_small || file.file_path).replace('/var/www/2.youngsam.net/backend/', '')}`}
                                      alt={file.file_name}
                                      className="w-full h-full object-cover pointer-events-none"
                                      style={{ imageOrientation: 'from-image' }}
                                      loading="lazy"
                                    />
                                  </picture>
                                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all"></div>
                                </div>
                              ))}
                            </div>
                            {scrollStates.projectImages.canScrollLeft && (
                              <button
                                onClick={() => {
                                  const container = document.getElementById('project-images-scroll');
                                  container.scrollLeft -= 200;
                                }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-r-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                              >
                                <ChevronLeftIcon className="h-4 w-4 text-blue-600" />
                              </button>
                            )}
                            {scrollStates.projectImages.canScrollRight && (
                              <button
                                onClick={() => {
                                  const container = document.getElementById('project-images-scroll');
                                  container.scrollLeft += 200;
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-l-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                              >
                                <ChevronRightIcon className="h-4 w-4 text-blue-600" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {projectDocs.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 mb-2">문서파일 ({projectDocs.length}개)</p>
                          <div className="relative group">
                            {projectDocs.length <= 3 ? (
                              // 3개 이하일 때 - 스크롤 없이 전체 너비 사용
                              <div className="flex flex-col gap-2">
                                {projectDocs.map((file, index) => {
                                  const ext = file.file_name.split('.').pop().toLowerCase();
                                  const fileTypeConfig = getFileTypeConfig(ext);
                                  
                                  return (
                                    <a
                                      key={index}
                                      href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                      download={file.file_name}
                                      className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors border border-blue-100 w-full"
                                    >
                                      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${fileTypeConfig.bgColor}`}>
                                        <DocumentTextIcon className={`h-4 w-4 ${fileTypeConfig.color}`} />
                                      </div>
                                      <div className="flex-1 min-w-0 overflow-hidden">
                                        <p className="text-sm text-gray-900 truncate" title={file.file_name}>
                                          {file.file_name.length > 30 ? 
                                            `${file.file_name.slice(0, 20)}...${file.file_name.slice(-7)}` : 
                                            file.file_name}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          {fileTypeConfig.label} • {(file.file_size / 1024 / 1024).toFixed(1)}MB
                                        </p>
                                      </div>
                                    </a>
                                  );
                                })}
                              </div>
                            ) : (
                              // 4개 이상일 때 - 기존 스크롤 방식
                              <>
                                <div 
                                  id="project-docs-scroll"
                                  className="overflow-x-auto scrollbar-hide"
                                  style={{ scrollBehavior: 'smooth' }}
                                  onScroll={() => checkScrollState('project-docs-scroll', 'projectDocs')}
                                >
                                  <div className="flex gap-4" style={{ width: 'max-content' }}>
                                    {Array.from({ length: Math.ceil(projectDocs.length / 3) }).map((_, groupIndex) => (
                                      <div key={groupIndex} className="flex flex-col gap-2">
                                        {projectDocs.slice(groupIndex * 3, (groupIndex + 1) * 3).map((file, index) => {
                                          const ext = file.file_name.split('.').pop().toLowerCase();
                                          const fileTypeConfig = getFileTypeConfig(ext);
                                          
                                          return (
                                            <a
                                              key={groupIndex * 3 + index}
                                              href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                              download={file.file_name}
                                              className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors border border-blue-100"
                                              style={{ width: '280px' }}
                                            >
                                              <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${fileTypeConfig.bgColor}`}>
                                                <DocumentTextIcon className={`h-4 w-4 ${fileTypeConfig.color}`} />
                                              </div>
                                              <div className="flex-1 min-w-0 overflow-hidden">
                                                <p className="text-sm text-gray-900 truncate" title={file.file_name}>
                                                  {file.file_name.length > 25 ? 
                                                    `${file.file_name.slice(0, 15)}...${file.file_name.slice(-7)}` : 
                                                    file.file_name}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                  {fileTypeConfig.label} • {(file.file_size / 1024 / 1024).toFixed(1)}MB
                                                </p>
                                              </div>
                                            </a>
                                          );
                                        })}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                {/* 좌측 화살표 */}
                                {scrollStates.projectDocs.canScrollLeft && (
                                  <button
                                    onClick={() => {
                                      const container = document.getElementById('project-docs-scroll');
                                      container.scrollLeft -= 300;
                                    }}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-r-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                                  >
                                    <ChevronLeftIcon className="h-4 w-4 text-blue-600" />
                                  </button>
                                )}
                                {scrollStates.projectDocs.canScrollRight && (
                                  <button
                                    onClick={() => {
                                      const container = document.getElementById('project-docs-scroll');
                                      container.scrollLeft += 300;
                                    }}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-l-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                                  >
                                    <ChevronRightIcon className="h-4 w-4 text-blue-600" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* 상세 내용 */}
            {project.description && (
              <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">상세 내용</h3>
                <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-gray-800 mb-2">{project.title}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{project.description}</p>
                </div>
              </div>
            )}

            {/* 소통 첨부파일 미리보기 */}
            {(() => {
              const allImages = getAllImages();
              const documentFiles = comments
                .filter(comment => !comment.is_deleted || user?.user_type === 'admin')
                .flatMap(comment => 
                  (comment.MessageFiles || []).map(file => ({
                    ...file,
                    isDeleted: comment.is_deleted
                  }))
                )
                .filter(file => file.file_type === 'document');
              
              return (allImages.length > 0 || documentFiles.length > 0) && (
                <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">소통 첨부파일</h3>
                    <button
                      onClick={() => setActiveTab('files')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      전체보기
                    </button>
                  </div>
                  
                  {allImages.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">이미지 ({allImages.length}개)</p>
                      <div className="relative group">
                        <div 
                          id="chat-images-scroll"
                          className="flex gap-2 overflow-x-auto scrollbar-hide pb-2"
                          style={{ scrollBehavior: 'smooth' }}
                          onScroll={() => checkScrollState('chat-images-scroll', 'chatImages')}
                        >
                          {allImages.map((file, index) => (
                            <div
                              key={index}
                              className="relative w-20 h-20 flex-shrink-0 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 select-none border border-blue-100 transition-opacity"
                              onClick={() => {
                                const allImages = getAllImages();
                                const index = allImages.findIndex(img => img.id === file.id);
                                setSelectedImage(file);
                                setSelectedImageIndex(index >= 0 ? index : 0);
                                setShowImageGallery(true);
                              }}
                            >
                              <picture>
                                {file.thumbnail_small_webp && (
                                  <source 
                                    srcSet={`/${file.thumbnail_small_webp.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                    type="image/webp" 
                                  />
                                )}
                                <img
                                  src={`/${(file.thumbnail_small || file.file_path).replace('/var/www/2.youngsam.net/backend/', '')}`}
                                  alt={file.file_name}
                                  className={`w-full h-full object-cover pointer-events-none ${file.isDeleted ? 'opacity-50' : ''}`}
                                  style={{ imageOrientation: 'from-image' }}
                                  loading="lazy"
                                />
                              </picture>
                              {file.isDeleted ? (
                                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                                  <span className="text-white text-xs font-medium bg-red-500 px-1 py-0.5 rounded">삭제됨</span>
                                </div>
                              ) : (
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all"></div>
                              )}
                            </div>
                          ))}
                        </div>
                        {scrollStates.chatImages.canScrollLeft && (
                          <button
                            onClick={() => {
                              const container = document.getElementById('chat-images-scroll');
                              container.scrollLeft -= 200;
                            }}
                            className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-r-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                          >
                            <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
                          </button>
                        )}
                        {scrollStates.chatImages.canScrollRight && (
                          <button
                            onClick={() => {
                              const container = document.getElementById('chat-images-scroll');
                              container.scrollLeft += 200;
                            }}
                            className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-l-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                          >
                            <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {documentFiles.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">문서파일 ({documentFiles.length}개)</p>
                      <div className="relative group">
                        {documentFiles.length <= 3 ? (
                          // 3개 이하일 때 - 스크롤 없이 전체 너비 사용
                          <div className="flex flex-col gap-2">
                            {documentFiles.map((file, index) => {
                              const ext = file.file_name.split('.').pop().toLowerCase();
                              const fileTypeConfig = getFileTypeConfig(ext);
                              
                              return (
                                <a
                                  key={index}
                                  href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                  download={file.file_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors w-full border ${
                                    file.isDeleted 
                                      ? 'bg-gray-50 hover:bg-gray-100 border-gray-300 opacity-60' 
                                      : 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-blue-100'
                                  }`}
                                >
                                  <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${fileTypeConfig.bgColor}`}>
                                    <DocumentTextIcon className={`h-3.5 w-3.5 ${fileTypeConfig.color}`} />
                                  </div>
                                  <div className="flex-1 min-w-0 flex items-center gap-2">
                                    <p className={`text-sm truncate flex-1 ${
                                      file.isDeleted ? 'text-gray-500 line-through' : 'text-gray-900'
                                    }`}>{file.file_name}</p>
                                    <p className="text-xs text-gray-500 flex-shrink-0">{fileTypeConfig.label} • {(file.file_size / 1024 / 1024).toFixed(1)}MB</p>
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          // 4개 이상일 때 - 기존 스크롤 방식
                          <>
                            <div 
                              id="chat-docs-scroll"
                              className="overflow-x-auto scrollbar-hide"
                              style={{ scrollBehavior: 'smooth' }}
                              onScroll={() => checkScrollState('chat-docs-scroll', 'chatDocs')}
                            >
                              <div className="flex gap-4" style={{ width: 'max-content' }}>
                                {Array.from({ length: Math.ceil(documentFiles.length / 3) }).map((_, groupIndex) => (
                                  <div key={groupIndex} className="flex flex-col gap-2">
                                    {documentFiles.slice(groupIndex * 3, (groupIndex + 1) * 3).map((file, index) => {
                                      const ext = file.file_name.split('.').pop().toLowerCase();
                                      const fileTypeConfig = getFileTypeConfig(ext);
                                      
                                      return (
                                        <a
                                          key={groupIndex * 3 + index}
                                          href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                          download={file.file_name}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors border border-blue-100"
                                          style={{ width: '280px' }}
                                        >
                                          <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${fileTypeConfig.bgColor}`}>
                                            <DocumentTextIcon className={`h-4 w-4 ${fileTypeConfig.color}`} />
                                          </div>
                                          <div className="flex-1 min-w-0 flex items-center gap-2">
                                            <p className="text-sm text-gray-900 truncate flex-1">{file.file_name}</p>
                                            <p className="text-xs text-gray-500 flex-shrink-0">{fileTypeConfig.label} • {(file.file_size / 1024 / 1024).toFixed(1)}MB</p>
                                          </div>
                                        </a>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* 좌측 화살표 */}
                            {scrollStates.chatDocs.canScrollLeft && (
                              <button
                                onClick={() => {
                                  const container = document.getElementById('chat-docs-scroll');
                                  container.scrollLeft -= 300;
                                }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-r-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                              >
                                <ChevronLeftIcon className="h-4 w-4 text-blue-600" />
                              </button>
                            )}
                            {scrollStates.chatDocs.canScrollRight && (
                              <button
                                onClick={() => {
                                  const container = document.getElementById('chat-docs-scroll');
                                  container.scrollLeft += 300;
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-l-lg p-1.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 border border-blue-100"
                              >
                                <ChevronRightIcon className="h-4 w-4 text-blue-600" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        )}

        {/* 소통 탭 */}
        {activeTab === 'chat' && (
          <div className="fixed inset-x-0 z-40" style={{ 
            top: `${statusBarHeight + 97}px`,
            bottom: '64px'
          }}>
            <MessageList 
              projectId={id}
              scrollToBottomTrigger={activeTab === 'chat'}
              onFilesUploaded={() => {
                // 프로젝트 정보 새로고침
                refetch();
                // 댓글 목록도 새로고침
                loadComments();
              }}
            />
          </div>
        )}

        {/* 기존 소통 탭 내용 (주석 처리) */}
        {false && (
          <div className="flex flex-col h-[calc(100vh-116px)]">
            {/* 채팅 헤더 */}
            <div className="p-4 bg-white border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-1 border border-blue-200">
                  <button
                    onClick={() => setMessageFilter('all')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      messageFilter === 'all' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    전체
                  </button>
                  <button
                    onClick={() => setMessageFilter('images')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      messageFilter === 'images' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    이미지
                  </button>
                  <button
                    onClick={() => setMessageFilter('documents')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      messageFilter === 'documents' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    문서
                  </button>
                </div>
              </div>
            </div>

            {/* 메시지 목록 - 개선된 배경 패턴 */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-hide relative" style={{
              background: 'linear-gradient(180deg, #EFF6FF 0%, #F0F9FF 50%, #FFFFFF 100%)',
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 50% 50%, rgba(147, 197, 253, 0.02) 0%, transparent 70%)
              `.trim()
            }}>
              {filteredMessages.length === 0 ? (
                <div className="text-center py-8">
                  <ChatBubbleBottomCenterTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    {messageFilter === 'all' 
                      ? '아직 메시지가 없습니다' 
                      : `${messageFilter === 'images' ? '이미지' : '문서'}가 없습니다`}
                  </p>
                </div>
              ) : (
                filteredMessages.map((comment, index) => {
                  // 이전 메시지와 날짜 비교
                  const currentDate = new Date(comment.created_at).toDateString();
                  const previousDate = index > 0 ? new Date(filteredMessages[index - 1].created_at).toDateString() : null;
                  const showDateDivider = !previousDate || currentDate !== previousDate;
                  
                  return (
                    <React.Fragment key={comment.id || index}>
                      {/* 날짜 구분선 */}
                      {showDateDivider && (
                        <div className="flex items-center gap-4 my-6">
                          <div className="flex-1 h-px bg-gray-200"></div>
                          <span className="text-xs text-gray-500 font-medium px-3 py-1 bg-gray-50 rounded-full">
                            {format(new Date(comment.created_at), 'yyyy년 M월 d일 EEEE', { locale: ko })}
                          </span>
                          <div className="flex-1 h-px bg-gray-200"></div>
                        </div>
                      )}
                      
                      {/* 연속 메시지 확인 */}
                      {(() => {
                        const prevComment = index > 0 ? filteredMessages[index - 1] : null;
                        const isSameUser = prevComment && prevComment.User?.id === comment.User?.id;
                        const timeDiff = prevComment ? 
                          (new Date(comment.created_at) - new Date(prevComment.created_at)) / 1000 / 60 : 999;
                        const isConsecutive = isSameUser && timeDiff < 5; // 5분 이내 연속 메시지
                        
                        // 다음 메시지 확인 (아바타 표시 여부 결정)
                        const nextComment = index < filteredMessages.length - 1 ? filteredMessages[index + 1] : null;
                        const isLastInGroup = !nextComment || 
                          nextComment.User?.id !== comment.User?.id || 
                          (new Date(nextComment.created_at) - new Date(comment.created_at)) / 1000 / 60 >= 5;
                        
                        // 아바타 색상 결정
                        const getAvatarColor = (userType) => {
                          switch(userType) {
                            case 'contractor': return 'bg-blue-500';
                            case 'customer':
                            case 'vip_customer': return 'bg-green-500';
                            case 'admin': return 'bg-purple-500';
                            default: return 'bg-gray-500';
                          }
                        };
                        
                        // 이니셜 추출
                        const getInitial = (name, userType) => {
                          if (!name) return '?';
                          if (userType === 'contractor') return '소';
                          if (userType === 'customer' || userType === 'vip_customer') return '고';
                          if (userType === 'admin') return '관';
                          return name.charAt(0);
                        };
                        
                        return (
                          <div 
                            id={`message-${comment.id}`}
                            className={`${
                              comment.User?.id === user?.id ? 'flex justify-end' : 'flex justify-start gap-2'
                            } ${isConsecutive ? 'mt-1' : 'mt-4'} transition-colors duration-500`}>
                            {/* 상대방 아바타 (왼쪽, 마지막 메시지에만) */}
                            {comment.User?.id !== user?.id && (
                              <div className="w-8 flex-shrink-0">
                                {isLastInGroup && (
                                  <div className={`w-8 h-8 rounded-full ${getAvatarColor(comment.User?.user_type)} flex items-center justify-center text-white text-xs font-bold`}>
                                    {getInitial(comment.User?.name, comment.User?.user_type)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className={`max-w-[85%] ${
                              comment.User?.id === user?.id ? 'order-2' : ''
                            }`}>
                              {/* 답글 표시 - 개선된 디자인 */}
                              {comment.replyTo && (
                                <div className={`mb-2 ${comment.User?.id === user?.id ? 'text-right' : ''}`}>
                                  <div 
                                    className={`inline-block p-2 bg-gray-100 border-l-4 border-blue-400 rounded-lg text-xs cursor-pointer hover:bg-gray-200 transition-colors ${
                                      comment.User?.id === user?.id ? 'ml-auto' : ''
                                    }`}
                                    onClick={() => {
                                      // 원본 메시지로 스크롤
                                      const originalMessage = document.getElementById(`message-${comment.reply_to_id}`);
                                      if (originalMessage) {
                                        originalMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        // 하이라이트 효과
                                        originalMessage.classList.add('bg-yellow-100');
                                        setTimeout(() => {
                                          originalMessage.classList.remove('bg-yellow-100');
                                        }, 2000);
                                      }
                                    }}
                                  >
                                    <div className="flex items-center gap-1 text-gray-600 mb-1">
                                      <ArrowUturnLeftIcon className="h-3 w-3 text-blue-500" />
                                      <span className="font-medium text-gray-700">
                                        {comment.replyTo.User?.name || '익명'}
                                        {comment.replyTo.User?.user_type === 'contractor' && ' 소장님'}
                                        {comment.replyTo.User?.user_type === 'customer' && ' 고객님'}
                                      </span>
                                    </div>
                                    <p className="text-gray-600 line-clamp-2 italic">
                                      "{comment.replyTo.content}"
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* 메시지 본문 */}
                              <div className={`group ${
                                comment.User?.id === user?.id ? 'text-right' : ''
                              }`}>
                                {/* 이름과 시간 표시 - 연속 메시지가 아닐 때만 */}
                                {!isConsecutive && (
                                  <div className={`flex items-end gap-2 mb-1 ${
                                    comment.User?.id === user?.id ? 'justify-end' : ''
                                  }`}>
                                    {comment.User?.id !== user?.id && (
                                      <span className="text-xs font-medium text-gray-700">
                                        {comment.User?.name || '익명'}
                                        {comment.User?.user_type === 'contractor' && ' 소장님'}
                                        {comment.User?.user_type === 'customer' && ' 고객님'}
                                        {comment.User?.user_type === 'admin' && ' (매니저)'}
                                      </span>
                                    )}
                                  </div>
                                )}
                                
                                {/* 시간 표시 - 메시지 그룹의 마지막에만 표시 */}
                                {isLastInGroup && (
                                  <div className={`flex items-center gap-1 mt-1 ${
                                    comment.User?.id === user?.id ? 'justify-end' : ''
                                  }`}>
                                    <span className="text-xs text-gray-400">
                                      {format(new Date(comment.created_at), 'HH:mm')}
                                    </span>
                                    {/* 내 메시지일 때만 읽음 표시 */}
                                    {comment.User?.id === user?.id && (
                                      <span className="text-xs text-blue-400" title={comment.is_read ? "읽음" : "전송됨"}>
                                        {comment.is_read ? '✓✓' : '✓'}
                                      </span>
                                    )}
                                  </div>
                                )}
                        
                        <div className={`inline-block relative ${
                          comment.is_deleted 
                            ? 'bg-gray-100 text-gray-400 opacity-75'
                            : comment.User?.id === user?.id 
                              ? 'bg-blue-500 text-white shadow-lg shadow-blue-200/50 hover:shadow-xl' 
                              : 'bg-white text-gray-900 shadow-md shadow-gray-200/50 hover:shadow-lg'
                        } rounded-2xl px-4 py-3 transition-shadow duration-200`}>
                          {/* 말풍선 꼬리 - 첫 메시지나 비연속 메시지에만 표시 */}
                          {!isConsecutive && (
                            comment.User?.id === user?.id ? (
                              // 내 메시지 꼬리 (오른쪽)
                              <div className="absolute -right-1.5 top-4">
                                <svg width="8" height="13" viewBox="0 0 8 13" className="fill-blue-500">
                                  <path d="M0.5 0 L8 0 Q0.5 6.5 0.5 13 Z"/>
                                </svg>
                              </div>
                            ) : (
                              // 상대방 메시지 꼬리 (왼쪽)
                              <div className="absolute -left-1.5 top-4">
                                <svg width="8" height="13" viewBox="0 0 8 13" className="fill-white drop-shadow-sm">
                                  <path d="M7.5 0 L0 0 Q7.5 6.5 7.5 13 Z"/>
                                </svg>
                              </div>
                            )
                          )}
                          {comment.content && (
                            <div>
                              {/* 삭제된 메시지 표시 (관리자만) */}
                              {comment.is_deleted && user?.user_type === 'admin' && (
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                    삭제됨
                                  </span>
                                  {comment.deleted_at && (
                                    <span className="text-xs text-gray-500">
                                      {format(new Date(comment.deleted_at), 'yyyy-MM-dd HH:mm')}
                                    </span>
                                  )}
                                </div>
                              )}
                              <p className={`whitespace-pre-wrap break-words ${
                                comment.is_deleted ? 'line-through opacity-60' : ''
                              }`}>
                                {comment.content}
                              </p>
                            </div>
                          )}
                          
                          {/* 첨부 파일 - 이미지 그룹 레이아웃 개선 */}
                          {comment.MessageFiles && comment.MessageFiles.length > 0 && (() => {
                            const imageFiles = comment.MessageFiles.filter(f => f.file_type === 'image');
                            const docFiles = comment.MessageFiles.filter(f => f.file_type !== 'image');
                            
                            return (
                              <div className="mt-2">
                                {/* 이미지 파일 그리드 */}
                                {imageFiles.length > 0 && (
                                  <div className={`${imageFiles.length === 1 ? 'block' : 'grid grid-cols-2 gap-1'} max-w-sm`}>
                                    {imageFiles.slice(0, 4).map((file, idx) => (
                                      <div key={file.id} className="relative">
                                        <picture
                                          className="block cursor-pointer"
                                          onClick={() => {
                                            const allImages = getAllImages();
                                            const index = allImages.findIndex(img => img.id === file.id);
                                            setSelectedImage(file);
                                            setSelectedImageIndex(index >= 0 ? index : 0);
                                            setShowImageGallery(true);
                                          }}
                                        >
                                          {file.thumbnail_medium_webp && (
                                            <source 
                                              srcSet={`/${file.thumbnail_medium_webp.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                              type="image/webp" 
                                            />
                                          )}
                                          <img 
                                            src={`/${(file.thumbnail_medium || file.file_path).replace('/var/www/2.youngsam.net/backend/', '')}`}
                                            alt={file.file_name}
                                            className={`rounded-lg ${
                                              imageFiles.length === 1 
                                                ? 'max-w-full max-h-64' 
                                                : 'w-full h-28 sm:h-32 object-cover'
                                            }`}
                                            loading="lazy"
                                          />
                                        </picture>
                                        {/* 4번째 이미지에 +N 오버레이 */}
                                        {idx === 3 && imageFiles.length > 4 && (
                                          <div 
                                            className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center cursor-pointer"
                                            onClick={() => {
                                              const allImages = getAllImages();
                                              const index = allImages.findIndex(img => img.id === file.id);
                                              setSelectedImage(file);
                                              setSelectedImageIndex(index >= 0 ? index : 0);
                                              setShowImageGallery(true);
                                            }}
                                          >
                                            <span className="text-white font-bold text-2xl">
                                              +{imageFiles.length - 4}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* 문서 파일 목록 */}
                                {docFiles.length > 0 && (
                                  <div className={`${imageFiles.length > 0 ? 'mt-2' : ''} space-y-1`}>
                                    {docFiles.map((file) => (
                                      <a 
                                        key={file.id}
                                        href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`} 
                                        download={file.file_name}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                                          comment.User?.id === user?.id 
                                            ? 'bg-blue-600 hover:bg-blue-700' 
                                            : 'bg-blue-100 hover:bg-blue-200'
                                        } transition-colors`}
                                      >
                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                        <span className="text-sm">{file.file_name}</span>
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        
                        {/* 버튼 그룹 - 답글과 삭제 */}
                        <div className={`inline-flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-all duration-200 ${
                          comment.User?.id === user?.id ? 'mr-2' : 'ml-2'
                        }`}>
                          {/* 답글 버튼 */}
                          <button
                            onClick={() => handleReply(comment)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg"
                          >
                            <ArrowUturnLeftIcon className="h-3 w-3" />
                            <span>답글</span>
                          </button>
                          
                          {/* 삭제 버튼 - 자신의 메시지이거나 관리자인 경우 */}
                          {(() => {
                            // 디버깅용 로그
                            console.log('Delete button check:', {
                              commentUserId: comment.User?.id,
                              currentUserId: user?.id,
                              userType: user?.user_type,
                              isDeleted: comment.is_deleted,
                              isOwner: comment.User?.id === user?.id,
                              isAdmin: user?.user_type === 'admin',
                              shouldShow: ((comment.User?.id === user?.id) || (user?.user_type === 'admin')) && !comment.is_deleted
                            });
                            
                            if (((comment.User?.id === user?.id) || (user?.user_type === 'admin')) && !comment.is_deleted) {
                              return (
                                <button
                                  onClick={() => handleDeleteMessage(comment.id)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-lg"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                  <span>삭제</span>
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </React.Fragment>
                  );
                })
              )}
              <div ref={commentsEndRef} />
            </div>

            {/* 답글 대상 표시 */}
            {replyTo && (
              <div className="px-4 py-1.5 bg-blue-50 border-t border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowUturnLeftIcon className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-blue-700">
                      {replyTo.User?.name || replyTo.user?.name}님에게 답글
                    </span>
                  </div>
                  <button
                    onClick={cancelReply}
                    className="p-1 hover:bg-blue-100 rounded transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4 text-blue-500" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1 truncate">
                  {replyTo.content}
                </p>
              </div>
            )}

            {/* 입력 영역 */}
            <div className="bg-white border-t border-gray-200">
              <form onSubmit={handleCommentSubmit} className="px-4 pb-0 pt-1.5">
                {/* 첨부 파일 미리보기 */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        {file.type.startsWith('image/') ? (
                          <div className="relative">
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt={file.name}
                              className="h-16 w-16 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="relative bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg px-3 py-2 pr-8 border border-blue-200">
                            <span className="text-xs text-gray-600 truncate max-w-[100px] block">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute right-1 top-1 text-gray-400 hover:text-red-500"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <textarea
                      ref={textareaRef}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleCommentSubmit(e);
                        }
                      }}
                      placeholder="메시지를 입력하세요..."
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12 break-words border border-blue-200"
                      rows={1}
                      style={{ 
                        minHeight: '48px',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        WebkitScrollbar: 'none',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    />
                    <label className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.hwp,.hwpx"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <PaperClipIcon className="h-5 w-5 text-gray-500 hover:text-gray-700 transition-colors" />
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={!newComment.trim() && selectedFiles.length === 0}
                    className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 파일 갤러리 탭 */}
        {activeTab === 'files' && (
          <div className="p-4 space-y-4">
            {/* 프로젝트 첨부파일 섹션 */}
            {project?.ProjectFiles && project.ProjectFiles.filter(file => file.category === 'initial').length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">프로젝트 첨부파일</h3>
                  {project.ProjectFiles.filter(file => file.category === 'initial').length > 0 && (
                    <button
                      onClick={() => handleBulkDownload('initial')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-lg transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      전체 다운로드
                    </button>
                  )}
                </div>
                
                {/* 프로젝트 이미지 */}
                {(() => {
                  const projectImages = project.ProjectFiles.filter(file => file.category === 'initial' && file.file_type === 'image');
                  const projectDocs = project.ProjectFiles.filter(file => file.category === 'initial' && file.file_type === 'document');
                  
                  return (
                    <>
                      {projectImages.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">이미지 ({projectImages.length}개)</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {projectImages.map((file, index) => (
                              <button
                                key={index}
                                onClick={() => {
                                  const allImages = getAllImages();
                                  const index = allImages.findIndex(img => img.id === file.id);
                                  setSelectedImage(file);
                                  setSelectedImageIndex(index >= 0 ? index : 0);
                                  setShowImageGallery(true);
                                }}
                                className="aspect-square overflow-hidden rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 hover:opacity-90 transition-opacity border border-blue-100"
                              >
                                <picture>
                                  {file.thumbnail_medium_webp && (
                                    <source 
                                      srcSet={`/${file.thumbnail_medium_webp.replace('/var/www/2.youngsam.net/backend/', '').replace(/^uploads\//, '')}`}
                                      type="image/webp" 
                                    />
                                  )}
                                  <img 
                                    src={`/${(file.thumbnail_medium || file.file_path).replace('/var/www/2.youngsam.net/backend/', '').replace(/^uploads\//, '')}`}
                                    alt={file.file_name}
                                    className="w-full h-full object-cover"
                                    style={{ imageOrientation: 'from-image' }}
                                    loading="lazy"
                                    onError={(e) => {
                                      // 경로가 잘못된 경우 대체 경로 시도
                                      if (!e.target.dataset.retried) {
                                        e.target.dataset.retried = 'true';
                                        const fallbackPath = file.file_path.includes('uploads/') 
                                          ? `/${file.file_path}` 
                                          : `/uploads/projects/${file.file_path}`;
                                        e.target.src = fallbackPath;
                                      }
                                    }}
                                  />
                                </picture>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {projectDocs.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">문서 ({projectDocs.length}개)</h4>
                          <div className="space-y-2">
                            {projectDocs.map((file, index) => {
                              const ext = file.file_name.split('.').pop().toLowerCase();
                              const fileTypeConfig = getFileTypeConfig(ext);
                              
                              return (
                                <a
                                  key={index}
                                  href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                                  download={file.file_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors border border-blue-100"
                                >
                                  <div className="flex-shrink-0">
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: fileTypeConfig.bgColor }}>
                                      <DocumentTextIcon className={`h-5 w-5 ${fileTypeConfig.color}`} />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <p className="text-sm font-medium text-gray-900 truncate" title={file.file_name}>
                                      {file.file_name.length > 40 ? 
                                        `${file.file_name.slice(0, 25)}...${file.file_name.slice(-10)}` : 
                                        file.file_name}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1">
                                      <span className="text-xs text-gray-500">
                                        {(file.file_size / 1024 / 1024).toFixed(1)}MB
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {fileTypeConfig.label}
                                      </span>
                                    </div>
                                  </div>
                                  <svg className="h-5 w-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* 소통 이미지 섹션 */}
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">소통 이미지</h3>
                  <span className="text-sm text-gray-500">({getAllImages().length}개)</span>
                </div>
                {getAllImages().length > 0 && (
                  <div className="relative">
                    {user?.user_type === 'admin' ? (
                      // 관리자용 드롭다운
                      <>
                        <button
                          onClick={() => setShowDeletedOptions(prev => ({ ...prev, image: !prev.image }))}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-lg transition-colors"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          전체 다운로드
                          <ChevronDownIcon className={`h-3 w-3 ml-1 transition-transform ${showDeletedOptions.image ? 'rotate-180' : ''}`} />
                        </button>
                        {showDeletedOptions.image && (
                          <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-10">
                            <button
                              onClick={() => {
                                handleBulkDownload('image');
                                setShowDeletedOptions(prev => ({ ...prev, image: false }));
                              }}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 w-full text-left"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              활성 파일만 다운로드
                            </button>
                            {(() => {
                              const deletedImages = comments
                                .filter(comment => comment.is_deleted)
                                .flatMap(comment => comment.MessageFiles || [])
                                .filter(file => file.file_type === 'image');
                              return deletedImages.length > 0 && (
                                <button
                                  onClick={() => {
                                    handleBulkDownload('image', true);
                                    setShowDeletedOptions(prev => ({ ...prev, image: false }));
                                  }}
                                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 w-full text-left border-t border-gray-100"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  삭제 포함 다운로드 ({deletedImages.length}개)
                                </button>
                              );
                            })()}
                          </div>
                        )}
                      </>
                    ) : (
                      // 일반 사용자용
                      <button
                        onClick={() => handleBulkDownload('image')}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-lg transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        전체 다운로드
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* 보기 옵션 - 제목 아래 별도 줄 */}
              {getAllImages().length > 0 && (
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setImageViewSettings(prev => ({ ...prev, thumbnailSize: 'small', currentPage: 1 }))}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${
                          imageViewSettings.thumbnailSize === 'small' 
                            ? 'bg-white text-blue-600 font-medium shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        작게
                      </button>
                      <button
                        onClick={() => setImageViewSettings(prev => ({ ...prev, thumbnailSize: 'medium', currentPage: 1 }))}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${
                          imageViewSettings.thumbnailSize === 'medium' 
                            ? 'bg-white text-blue-600 font-medium shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        중간
                      </button>
                    </div>
                  </div>
                  
                  <select
                    value={imageViewSettings.itemsPerPage}
                    onChange={(e) => setImageViewSettings(prev => ({ 
                      ...prev, 
                      itemsPerPage: Number(e.target.value),
                      currentPage: 1 
                    }))}
                    className="text-xs bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-100 transition-colors cursor-pointer appearance-none bg-no-repeat"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.2em 1.2em'
                    }}
                  >
                    <option value={20}>20개</option>
                    <option value={40}>40개</option>
                    <option value={60}>60개</option>
                    <option value={100}>100개</option>
                  </select>
                </div>
              )}
              {getAllImages().length > 0 ? (
                <>
                  {/* 이미지 그리드 - 크기에 따라 다른 그리드 설정 */}
                  <div className={`grid gap-3 ${
                    imageViewSettings.thumbnailSize === 'small' 
                      ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8'
                      : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
                  }`}>
                    {getAllImages()
                      .slice(
                        (imageViewSettings.currentPage - 1) * imageViewSettings.itemsPerPage,
                        imageViewSettings.currentPage * imageViewSettings.itemsPerPage
                      )
                      .map((image, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        const allImages = getAllImages();
                        const index = allImages.findIndex(img => img.id === image.id);
                        setSelectedImage(image);
                        setSelectedImageIndex(index >= 0 ? index : 0);
                        setShowImageGallery(true);
                      }}
                      className="aspect-square overflow-hidden rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 hover:opacity-90 transition-opacity border border-blue-100 relative cursor-pointer"
                    >
                      {/* 이미지는 항상 표시 */}
                      <picture>
                        {image.thumbnail_medium_webp && (
                          <source 
                            srcSet={`/${image.thumbnail_medium_webp.replace('/var/www/2.youngsam.net/backend/', '')}`}
                            type="image/webp" 
                          />
                        )}
                        <img 
                          src={`/${(image.thumbnail_medium || image.file_path).replace('/var/www/2.youngsam.net/backend/', '')}`}
                          alt={image.file_name}
                          className={`w-full h-full object-cover ${image.isDeleted ? 'opacity-50' : ''}`}
                          style={{ imageOrientation: 'from-image' }}
                          loading="lazy"
                        />
                      </picture>
                      
                      {/* 삭제된 경우 오버레이 표시 */}
                      {image.isDeleted && (
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                          <span className="text-white text-sm font-medium bg-red-500 px-2 py-1 rounded">삭제됨</span>
                        </div>
                      )}
                    </button>
                  ))}
                  </div>
                  
                  {/* 페이지네이션 */}
                  {Math.ceil(getAllImages().length / imageViewSettings.itemsPerPage) > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <button
                        onClick={() => setImageViewSettings(prev => ({ 
                          ...prev, 
                          currentPage: Math.max(1, prev.currentPage - 1) 
                        }))}
                        disabled={imageViewSettings.currentPage === 1}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          imageViewSettings.currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        ◀ 이전
                      </button>
                      
                      {/* 페이지 번호들 */}
                      <div className="flex items-center gap-1">
                        {(() => {
                          const totalPages = Math.ceil(getAllImages().length / imageViewSettings.itemsPerPage);
                          const currentPage = imageViewSettings.currentPage;
                          const pages = [];
                          
                          // 표시할 페이지 번호 계산
                          let startPage = Math.max(1, currentPage - 2);
                          let endPage = Math.min(totalPages, startPage + 4);
                          
                          // startPage 재조정
                          if (endPage - startPage < 4) {
                            startPage = Math.max(1, endPage - 4);
                          }
                          
                          // 첫 페이지
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => setImageViewSettings(prev => ({ ...prev, currentPage: 1 }))}
                                className="w-8 h-8 text-sm rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                1
                              </button>
                            );
                            if (startPage > 2) {
                              pages.push(<span key="dots1" className="px-1">...</span>);
                            }
                          }
                          
                          // 중간 페이지들
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => setImageViewSettings(prev => ({ ...prev, currentPage: i }))}
                                className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                                  i === currentPage
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          // 마지막 페이지
                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(<span key="dots2" className="px-1">...</span>);
                            }
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => setImageViewSettings(prev => ({ ...prev, currentPage: totalPages }))}
                                className="w-8 h-8 text-sm rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                {totalPages}
                              </button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                      
                      <button
                        onClick={() => setImageViewSettings(prev => ({ 
                          ...prev, 
                          currentPage: Math.min(
                            Math.ceil(getAllImages().length / prev.itemsPerPage), 
                            prev.currentPage + 1
                          ) 
                        }))}
                        disabled={imageViewSettings.currentPage === Math.ceil(getAllImages().length / imageViewSettings.itemsPerPage)}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          imageViewSettings.currentPage === Math.ceil(getAllImages().length / imageViewSettings.itemsPerPage)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        다음 ▶
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <PhotoIcon className="h-12 w-12 text-blue-300 mx-auto mb-3" />
                  <p className="text-blue-500">업로드된 이미지가 없습니다</p>
                </div>
              )}
            </div>

            {/* 소통 문서 섹션 */}
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">소통 문서</h3>
                  <span className="text-sm text-gray-500">
                    ({comments.filter(comment => !comment.is_deleted || user?.user_type === 'admin').flatMap(comment => comment.MessageFiles || []).filter(file => file.file_type === 'document').length}개)
                  </span>
                </div>
                {comments.filter(comment => !comment.is_deleted || user?.user_type === 'admin').flatMap(comment => comment.MessageFiles || []).filter(file => file.file_type === 'document').length > 0 && (
                  <div className="relative">
                    {user?.user_type === 'admin' ? (
                      // 관리자용 드롭다운
                      <>
                        <button
                          onClick={() => setShowDeletedOptions(prev => ({ ...prev, document: !prev.document }))}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-lg transition-colors"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          전체 다운로드
                          <ChevronDownIcon className={`h-3 w-3 ml-1 transition-transform ${showDeletedOptions.document ? 'rotate-180' : ''}`} />
                        </button>
                        {showDeletedOptions.document && (
                          <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-10">
                            <button
                              onClick={() => {
                                handleBulkDownload('document');
                                setShowDeletedOptions(prev => ({ ...prev, document: false }));
                              }}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 w-full text-left"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              활성 파일만 다운로드
                            </button>
                            {(() => {
                              const deletedDocs = comments
                                .filter(comment => comment.is_deleted)
                                .flatMap(comment => comment.MessageFiles || [])
                                .filter(file => file.file_type === 'document');
                              return deletedDocs.length > 0 && (
                                <button
                                  onClick={() => {
                                    handleBulkDownload('document', true);
                                    setShowDeletedOptions(prev => ({ ...prev, document: false }));
                                  }}
                                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 w-full text-left border-t border-gray-100"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  삭제 포함 다운로드 ({deletedDocs.length}개)
                                </button>
                              );
                            })()}
                          </div>
                        )}
                      </>
                    ) : (
                      // 일반 사용자용
                      <button
                        onClick={() => handleBulkDownload('document')}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-lg transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        전체 다운로드
                      </button>
                    )}
                  </div>
                )}
              </div>
              {(() => {
                const documentFiles = comments
                  .filter(comment => !comment.is_deleted || user?.user_type === 'admin')
                  .flatMap(comment => 
                    (comment.MessageFiles || []).map(file => ({
                      ...file,
                      userName: comment.User?.name,
                      date: comment.created_at,
                      isDeleted: comment.is_deleted,
                      deletedAt: comment.deleted_at
                    }))
                  ).filter(file => file.file_type === 'document');

                return documentFiles.length > 0 ? (
                  <div className="space-y-2">
                    {documentFiles.map((file, index) => (
                      <a
                        key={index}
                        href={`/${file.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                        download={file.file_name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 p-2 rounded-lg transition-colors border ${
                          file.isDeleted && user?.user_type === 'admin'
                            ? 'bg-gray-50 hover:bg-gray-100 border-gray-300 opacity-60'
                            : 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-blue-100'
                        }`}
                      >
                        <div className="flex-shrink-0">
                          {file.file_name.endsWith('.pdf') && (
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-red-600" />
                            </div>
                          )}
                          {(file.file_name.endsWith('.doc') || file.file_name.endsWith('.docx')) && (
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          )}
                          {(file.file_name.endsWith('.xls') || file.file_name.endsWith('.xlsx')) && (
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-green-600" />
                            </div>
                          )}
                          {(file.file_name.endsWith('.hwp') || file.file_name.endsWith('.hwpx')) && (
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                            </div>
                          )}
                          {file.file_name.endsWith('.zip') && (
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-slate-600" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className={`text-sm font-medium truncate ${
                            file.isDeleted && user?.user_type === 'admin' ? 'text-gray-500 line-through' : 'text-gray-900'
                          }`} title={file.file_name}>
                            {file.file_name.length > 35 ? 
                              `${file.file_name.slice(0, 20)}...${file.file_name.slice(-10)}` : 
                              file.file_name}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">
                              {(file.file_size / 1024 / 1024).toFixed(1)}MB
                            </span>
                            <span className="text-xs text-gray-500 truncate">
                              {file.userName} • {format(new Date(file.date), 'MM월 dd일 HH:mm')}
                            </span>
                          </div>
                        </div>
                        <svg className="h-5 w-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="h-12 w-12 text-blue-300 mx-auto mb-3" />
                    <p className="text-blue-500">업로드된 문서가 없습니다</p>
                  </div>
                );
              })()}
            </div>
            
            {/* 계약서 섹션 */}
            {project?.ProjectFiles && project.ProjectFiles.filter(file => file.category === 'contract').length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">계약서 및 관련 문서</h3>
                  {project.ProjectFiles.filter(file => file.category === 'contract').length > 0 && (
                    <button
                      onClick={() => handleBulkDownload('contract')}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-lg transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      전체 다운로드
                    </button>
                  )}
                </div>
                
                {(() => {
                  const contractFiles = project.ProjectFiles.filter(file => file.category === 'contract');
                  const contractImages = contractFiles.filter(file => file.file_type === 'image');
                  const contractDocs = contractFiles.filter(file => file.file_type === 'document');
                  
                  return (
                    <>
                      {contractImages.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">이미지 ({contractImages.length}개)</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {contractImages.map((file, index) => (
                              <button
                                key={index}
                                onClick={() => {
                                  const allImages = getAllImages();
                                  const index = allImages.findIndex(img => img.id === file.id);
                                  setSelectedImage(file);
                                  setSelectedImageIndex(index >= 0 ? index : 0);
                                  setShowImageGallery(true);
                                }}
                                className="aspect-square overflow-hidden rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 hover:opacity-90 transition-opacity border border-green-100"
                              >
                                <img 
                                  src={`/${(file.thumbnail_medium || file.file_path).replace('/var/www/2.youngsam.net/backend/', '').replace(/^uploads\//, '')}`}
                                  alt={file.file_name || file.file_path}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    // 경로가 잘못된 경우 대체 경로 시도
                                    if (!e.target.dataset.retried) {
                                      e.target.dataset.retried = 'true';
                                      const fallbackPath = file.file_path.includes('uploads/') 
                                        ? `/${file.file_path}` 
                                        : `/uploads/projects/${file.file_path}`;
                                      e.target.src = fallbackPath;
                                    }
                                  }}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {contractDocs.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">문서 ({contractDocs.length}개)</h4>
                          <div className="space-y-2">
                            {contractDocs.map((file, index) => {
                              const fileName = file.file_name || file.file_path || '';
                              const ext = fileName ? fileName.split('.').pop().toLowerCase() : '';
                              const fileTypeConfig = getFileTypeConfig(ext);
                              
                              return (
                                <a
                                  key={index}
                                  href={`/uploads/projects/${file.file_path}`}
                                  download={file.file_name || file.file_path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg hover:from-green-100 hover:to-emerald-100 transition-colors border border-green-100"
                                >
                                  <div className={`w-10 h-10 ${fileTypeConfig.bgColor} rounded-lg flex items-center justify-center`}>
                                    <DocumentTextIcon className={`h-5 w-5 ${fileTypeConfig.iconColor}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{file.file_name || file.file_path}</p>
                                    <div className="flex items-center gap-3 mt-1">
                                      <span className="text-xs text-gray-500">
                                        {(file.file_size / 1024 / 1024).toFixed(1)}MB
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {format(new Date(file.created_at), 'yyyy.MM.dd HH:mm')}
                                      </span>
                                    </div>
                                  </div>
                                  <ArrowDownTrayIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>


      {/* 이미지 갤러리 모달 - 전체 화면 */}
      {showImageGallery && (() => {
        const allImages = getAllImages();
        
        // 스와이프 처리
        const minSwipeDistance = 50;
        
        const onTouchStart = (e) => {
          setTouchEnd(null);
          setTouchStart(e.targetTouches[0].clientX);
        };
        
        const onTouchMove = (e) => {
          setTouchEnd(e.targetTouches[0].clientX);
        };
        
        const onTouchEnd = () => {
          if (!touchStart || !touchEnd) return;
          const distance = touchStart - touchEnd;
          const isLeftSwipe = distance > minSwipeDistance;
          const isRightSwipe = distance < -minSwipeDistance;
          
          if (isLeftSwipe && selectedImageIndex < allImages.length - 1) {
            setSelectedImageIndex(selectedImageIndex + 1);
            setSelectedImage(allImages[selectedImageIndex + 1]);
          }
          if (isRightSwipe && selectedImageIndex > 0) {
            setSelectedImageIndex(selectedImageIndex - 1);
            setSelectedImage(allImages[selectedImageIndex - 1]);
          }
        };
        
        return (
          <div className="fixed inset-0 z-[100] bg-black">
            <div className="relative h-full w-full">
              {/* 헤더 - 상단 고정 */}
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 z-20">
                <div className="flex items-center justify-between max-w-7xl mx-auto">
                  <h3 className="text-white font-semibold text-lg">
                    이미지 갤러리 ({selectedImageIndex + 1} / {allImages.length})
                  </h3>
                  <button
                    onClick={() => {
                      setShowImageGallery(false);
                      setSelectedImage(null);
                      setSelectedImageIndex(0);
                    }}
                    className="p-2.5 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 rounded-full transition-all"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* 이미지 뷰어 */}
              {selectedImage ? (
                <div 
                  className="h-full flex items-center justify-center"
                  onClick={(e) => {
                    // 이미지 외부 클릭 시 갤러리 닫기
                    if (e.target === e.currentTarget) {
                      setShowImageGallery(false);
                      setSelectedImage(null);
                      setSelectedImageIndex(0);
                    }
                  }}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {/* 좌측 화살표 (PC용) */}
                  {selectedImageIndex > 0 && (
                    <button
                      onClick={() => {
                        setSelectedImageIndex(selectedImageIndex - 1);
                        setSelectedImage(allImages[selectedImageIndex - 1]);
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 rounded-full transition-all z-10 hidden sm:block"
                    >
                      <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                  )}
                  
                  {/* 이미지 */}
                  <img 
                    src={`/${selectedImage.file_path.replace('/var/www/2.youngsam.net/backend/', '')}`}
                    alt={selectedImage.file_name}
                    className="max-w-full max-h-full object-contain px-4 py-20"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                  />
                  
                  {/* 우측 화살표 (PC용) */}
                  {selectedImageIndex < allImages.length - 1 && (
                    <button
                      onClick={() => {
                        setSelectedImageIndex(selectedImageIndex + 1);
                        setSelectedImage(allImages[selectedImageIndex + 1]);
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 rounded-full transition-all z-10 hidden sm:block"
                    >
                      <ChevronRightIcon className="h-6 w-6" />
                    </button>
                  )}
                  
                  {/* 하단 정보 */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-safe">
                    <div className="max-w-7xl mx-auto">
                      <p className="text-white text-sm truncate">{selectedImage.file_name}</p>
                      <p className="text-white/70 text-xs">
                        {selectedImage.userName} • {formatDate(selectedImage.date)}
                      </p>
                    </div>
                  </div>
                  
                  {/* 모바일 인디케이터 */}
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1 sm:hidden">
                    {allImages.map((_, index) => (
                      <div
                        key={index}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          index === selectedImageIndex ? 'bg-white w-4' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </div>
            ) : (
              /* 썸네일 그리드 */
              <div className="h-full overflow-y-auto p-4 pt-20 pb-safe">
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 max-w-7xl mx-auto">
                  {allImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedImage(image);
                        setSelectedImageIndex(index);
                      }}
                      className="aspect-square overflow-hidden rounded-lg bg-slate-900 relative group"
                    >
                      <img 
                        src={`/${(image.thumbnail_medium || image.file_path).replace('/var/www/2.youngsam.net/backend/', '')}`}
                        alt={image.file_name}
                        className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 상태 변경 모달 */}
      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={project?.status}
        onConfirm={handleStatusChange}
      />
      
      {/* 관리자 상태 직접 변경 모달 */}
      <AdminStatusModal
        isOpen={showAdminStatusModal}
        onClose={() => setShowAdminStatusModal(false)}
        currentStatus={project?.status}
        onConfirm={handleStatusChange}
      />
      
      {/* 취소된 프로젝트 안내 모달 */}
      <CancelledProjectModal
        isOpen={showCancelledModal}
        onClose={() => setShowCancelledModal(false)}
        project={project}
      />
    </div>
    
    {/* 조회 이력 모달 */}
    {viewLogsModal.show && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden border border-blue-200 shadow-xl">
          <div className="p-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="text-lg font-semibold">
              {viewLogsModal.userName} {viewLogsModal.userType === 'customer' ? '고객님' : '소장님'} 조회 이력
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              총 {viewLogsModal.userType === 'customer' 
                ? project?.customer_view_count 
                : project?.contractor_view_count}회 조회
            </p>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {viewLogs && viewLogs.length > 0 ? (
              <div className="space-y-3">
                {viewLogs.map((log, index) => (
                  <div key={log.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-700 border border-blue-200">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {log.formatted_time}
                      </p>
                      <p className="text-xs text-gray-500">
                        {log.relative_time}
                      </p>
                      {log.user_agent && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {log.user_agent.includes('Mobile') ? '📱 모바일' : '💻 데스크톱'}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                조회 이력이 없습니다.
              </p>
            )}
          </div>
          
          <div className="p-4 border-t border-blue-100">
            <button
              onClick={() => setViewLogsModal({
                show: false,
                projectId: null,
                userId: null,
                userName: '',
                userType: ''
              })}
              className="w-full px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors border border-blue-200"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* 평가 모달 */}
    {showRatingModal && project?.contractor && (
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onReviewComplete={() => {
          // 평가 완료 후 상태 다시 확인
          if (project?.status === 'completed') {
            checkRatingStatus();
          }
        }}
        contractorId={project.contractor.id}
        contractorName={project.contractor.name}
        currentProjectId={id}
      />
    )}

    {/* 평가 내용 보기 모달 (소장/관리자용) */}
    {showRatingViewModal && existingRating && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
          {/* 헤더 */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">고객 평가 내용</h2>
                <p className="text-green-100 text-sm mt-1">{project?.contractor?.name} 소장님</p>
              </div>
              <button
                onClick={() => setShowRatingViewModal(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* 내용 */}
          <div className="p-6">
            {/* 별점 표시 */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon
                    key={star}
                    className={`h-8 w-8 ${
                      star <= (existingRating?.rating || 0) 
                        ? 'text-yellow-400' 
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {existingRating?.rating || 0}.0점
              </p>
              <p className="text-sm text-gray-500">고객 평가 점수</p>
            </div>

            {/* 평가 코멘트 */}
            {existingRating?.comment && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">평가 내용</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-800 leading-relaxed">
                    {existingRating.comment}
                  </p>
                </div>
              </div>
            )}

            {/* 평가일시 */}
            <div className="text-center text-sm text-gray-500">
              평가일: {existingRating?.created_at ? 
                format(new Date(existingRating.created_at), 'yyyy년 MM월 dd일 HH:mm', { locale: ko }) :
                '정보 없음'
              }
            </div>
          </div>

          {/* 하단 버튼 */}
          <div className="p-5 border-t bg-gray-50">
            <button
              onClick={() => setShowRatingViewModal(false)}
              className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* 보증서 모달 */}
    {showWarrantyCertificate && warrantyData && (
      <WarrantyCertificate 
        warrantyData={warrantyData}
        onClose={() => {
          setShowWarrantyCertificate(false);
          setWarrantyData(null);
        }}
      />
    )}
    </SimplePullToRefresh>
  );
};

export default ProjectDetail;